import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [ctx, setCtx] = useState(null); // full auth context from /me
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setCtx(data);
      return data;
    } catch {
      setCtx(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me when returning from Google OAuth: AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {}
    setCtx(null);
  }, []);

  const value = {
    ctx,
    setCtx,
    loading,
    reload: loadMe,
    logout,
    user: ctx?.user || null,
    organizations: ctx?.organizations || [],
    organization: ctx?.current_organization || null,
    orgId: ctx?.current_organization?.id || null,
    role: ctx?.role || null,
    permissions: ctx?.permissions || [],
    features: ctx?.features || [],
    plan: ctx?.plan || null,
    limits: ctx?.limits || {},
    plans: ctx?.plans || {},
    can: (p) => (ctx?.permissions || []).includes(p),
    hasFeature: (f) => (ctx?.features || []).includes("*") || (ctx?.features || []).includes(f),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
